import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { Edit, Delete, Add, People, Palette } from '@mui/icons-material';
import { deepPurple, indigo, teal, pink, amber, cyan } from '@mui/material/colors';

// Couleurs disponibles pour les groupes


const GroupsListPage = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
const [editingGroup, setEditingGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({
    nom: "",
    description: "",
    color: deepPurple[500],
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [hoveredId, setHoveredId] = useState(null);
  const navigate = useNavigate();

  // Fonction utilitaire pour les appels API
  const apiRequest = async (endpoint, method = 'GET', body = null) => {
    const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}${endpoint}`;
    const accessToken = localStorage.getItem("accessToken");
    
    const options = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    };
    
    if (body) {
      options.body = JSON.stringify(body);
    }
    
    try {
      const resp = await fetch(url, options);
      const data = await resp.json().catch(() => ({})); // Gestion des réponses vides
      
      if (!resp.ok) {
        // Si le backend retourne un message d'erreur dans la réponse JSON
        const errorMsg = data.error || data.message || `HTTP error! Status: ${resp.status}`;
        throw new Error(errorMsg);
      }
      
      return data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  };

  // Chargement initial des données
  useEffect(() => {
    const fetchData = async () => {
      try {
        const groupsData = await apiRequest('/api/groupes/getAllGroupes/');
        setGroups(groupsData);
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Erreur lors du chargement des groupes',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, []);

  // Gestion de l'ajout d'un nouveau groupe
  const handleAddGroup = async () => {
    try {
      const data = await apiRequest('/api/groupes/create/', 'POST', {
        nom: newGroup.nom,
        description: newGroup.description,
        color: newGroup.color
      });

      setGroups([...groups, data]);
      setOpenAddDialog(false);
      setNewGroup({
        nom: "",
        description: "",
        color: deepPurple[500],
      });
      
      setSnackbar({
        open: true,
        message: 'Groupe créé avec succès',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Erreur lors de la création du groupe',
        severity: 'error'
      });
    }
  };

  // Gestion de la suppression d'un groupe
  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;
  
    try {
      const response = await apiRequest(`/api/groupes/delete/${groupToDelete}/`, 'DELETE');
      
      // Vérifiez si la suppression a réussi
      if (response.success !== false) {
        setGroups(prevGroups => prevGroups.filter(group => group.id !== groupToDelete));
        setSnackbar({
          open: true,
          message: response.message || 'Groupe supprimé avec succès',
          severity: 'success'
        });
      } else {
        throw new Error(response.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Erreur lors de la suppression du groupe',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
      setGroupToDelete(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const handleUpdateGroup = async () => {
    if (!editingGroup) return;
  
    try {
      const updatedGroup = await apiRequest(`/api/groupes/update/${editingGroup.id}/`, 'PUT', {
        nom: editingGroup.nom,
        description: editingGroup.description,
        color: editingGroup.color
      });
  
      setGroups(groups.map(group => 
        group.id === editingGroup.id ? updatedGroup : group
      ));
  
      setOpenEditDialog(false);
      setEditingGroup(null);
      
      setSnackbar({
        open: true,
        message: 'Groupe mis à jour avec succès',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Erreur lors de la mise à jour du groupe',
        severity: 'error'
      });
    }
  };

  const navigateToGroupDetails = (groupId) => {
    const tenant = localStorage.getItem('tenant');
    navigate(`/${tenant}/dashboard/group/${groupId}`);
  };

  const handleEditGroup = (groupId) => {
    const groupToEdit = groups.find(group => group.id === groupId);
    if (groupToEdit) {
      setEditingGroup(groupToEdit);
      setOpenEditDialog(true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '80vh',
        background: 'transparent',
        mt:5,
      }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (!groups.length) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '80vh',
        p: 3,
        textAlign: 'center',
        mt:6,
        background: 'transparent'
      }}>
        <People sx={{ fontSize: 80, mb: 2 }} />
        <Typography variant="h5" sx={{ mb: 2 }}>
          Aucun groupe disponible
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Commencez par créer votre premier groupe
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {console.log("hello"); setOpenAddDialog(true);}}
        >
          Créer un groupe
        </Button>
          {/* Dialog pour ajouter un nouveau groupe */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Typography variant="h6">
            Nouveau Groupe
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 , mt:4}}>
            <TextField
              fullWidth
              label="Nom du groupe *"
              value={newGroup.nom}
              onChange={(e) => setNewGroup({...newGroup, nom: e.target.value})}
              variant="outlined"
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={newGroup.description}
              onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
              variant="outlined"
            />
           
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setOpenAddDialog(false)} 
            color="inherit"
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleAddGroup} 
            disabled={!newGroup.nom}
            startIcon={<Add />}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 4 }}>
      {/* En-tête */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        mt: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Groupes
          </Typography>
          <Typography variant="body1">
          {groups.length} {groups.length === 1 ? 'groupe' : 'groupes'} disponible{groups.length > 1 ? 's' : ''}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenAddDialog(true)}
        >
          Nouveau groupe
        </Button>
      </Box>

      {/* Tableau des groupes */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><b>Nom</b></TableCell>
              <TableCell><b>Description</b></TableCell>
              <TableCell align="center"><b>Membres</b></TableCell>
              <TableCell align="right"><b>Options</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <TableRow
                key={group.id}
                hover
                onMouseEnter={() => setHoveredId(group.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
              <TableCell 
  sx={{ 
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' }
  }}
  onClick={() => navigateToGroupDetails(group.id)}
>
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    {group.nom}
  </Box>
</TableCell>

<TableCell>
  {group.description 
    ? group.description.length > 200 
      ? `${group.description.slice(0, 200)}...` 
      : group.description 
    : 'Aucune description'}
</TableCell>
                <TableCell align="center">
                  <Chip 
                    label={`${group.user_count || 0}`}
                    size="small"
                    icon={<People sx={{ fontSize: 16 }} />}
                  />
                </TableCell>
                <TableCell align="right" sx={{ width: 120 }}>
                  {hoveredId === group.id && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Tooltip title="Modifier">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditGroup(group.id);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setGroupToDelete(group.id);
                            setOpenDeleteDialog(true);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pour ajouter un nouveau groupe */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Typography variant="h6">
            Nouveau Groupe
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 , mt:4}}>
            <TextField
              fullWidth
              label="Nom du groupe *"
              value={newGroup.nom}
              onChange={(e) => setNewGroup({...newGroup, nom: e.target.value})}
              variant="outlined"
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={newGroup.description}
              onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
              variant="outlined"
            />
           
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setOpenAddDialog(false)} 
            color="inherit"
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleAddGroup} 
            disabled={!newGroup.nom}
            startIcon={<Add />}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          Êtes-vous sûr de vouloir supprimer ce groupe ? Cette action est irréversible.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteGroup}
            color="error"
            variant="contained"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog pour éditer un groupe */}
<Dialog 
  open={openEditDialog} 
  onClose={() => setOpenEditDialog(false)}
  maxWidth="sm" 
  fullWidth
>
  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Edit fontSize="small" />
    <Typography variant="h6">
      Modifier le groupe
    </Typography>
  </DialogTitle>
  <DialogContent sx={{ pt: 3 }}>
  {editingGroup && (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 4 }}>
      <TextField
        fullWidth
        label="Nom du groupe *"
        value={editingGroup.nom}
        onChange={(e) => setEditingGroup({ ...editingGroup, nom: e.target.value })}
        variant="outlined"
        InputProps={{
          sx: { fontWeight: 400 } // poids normal
        }}
        InputLabelProps={{
          sx: { fontWeight: 400 } // label non gras aussi
        }}
      />
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description"
        value={editingGroup.description}
        onChange={(e) => setEditingGroup({ ...editingGroup, description: e.target.value })}
        variant="outlined"
        InputProps={{
          sx: { fontWeight: 400 }
        }}
        InputLabelProps={{
          sx: { fontWeight: 400 }
        }}
      />
    </Box>
  )}
</DialogContent>

  <DialogActions sx={{ p: 3 }}>
    <Button 
      onClick={() => setOpenEditDialog(false)} 
      color="inherit"
    >
      Annuler
    </Button>
    <Button
      variant="contained"
      onClick={handleUpdateGroup}
      disabled={!editingGroup?.nom}
      startIcon={<Edit />}
    >
      Mettre à jour
    </Button>
  </DialogActions>
</Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupsListPage;